import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Copy, Eraser, Eye, EyeOff, Sparkles } from "lucide-react";
import { LocalesList } from "../types/Locales";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Button } from "./ui/button";
import LangBlock from "./LangBlock";
import { DyTextarea } from "./ui/dynamic-textarea";
import { JsonEditorActions } from "./JsonEditorActions";
import {
  EditorQuickFiltersState,
  TranslationRowModel,
  getNestedValue,
} from "../features/editorV2/utils/editorPresentationUtils";
import { useEditorViewModel } from "../features/editorV2/hooks/useEditorViewModel";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

interface JsonEditorFormProps {
  localesList: LocalesList[];
  onUpdate: (updatedJson: LocalesList[]) => void;
  onSave: (updatedJson: LocalesList[], commitMessage: string) => void;
  hasCommits?: boolean;
  isBusy?: boolean;
  searchQuery: string;
  quickFilters: EditorQuickFiltersState;
  activeBranch?: string;
  defaultBranch?: string;
}

const setNestedValue = (
  keyPath: string,
  value: string,
  source: Record<string, unknown>
): Record<string, unknown> => {
  const copy = { ...source };
  const keys = keyPath.split(".");
  let cursor: Record<string, unknown> = copy;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    const next = cursor[key];
    if (typeof next !== "object" || next === null || Array.isArray(next)) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  });

  return copy;
};

const JsonEditorForm: FC<JsonEditorFormProps> = ({
  localesList,
  onUpdate,
  onSave,
  hasCommits = false,
  isBusy = false,
  searchQuery,
  quickFilters,
  activeBranch,
  defaultBranch,
}) => {
  const [editedJson, setEditedJson] = useState<LocalesList[]>(localesList);
  const [isDirty, setIsDirty] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});
  const [areAllExpanded, setAreAllExpanded] = useState(false);
  const initialSnapshotRef = useRef<LocalesList[]>(localesList);

  useEffect(() => {
    setEditedJson(localesList);
    initialSnapshotRef.current = localesList;
    setIsDirty(false);
  }, [localesList]);

  const rows = useEditorViewModel({
    locales: editedJson,
    baselineLocales: initialSnapshotRef.current,
    searchQuery,
    quickFilters,
  });

  useEffect(() => {
    setExpandedKeys((prev) => {
      const next: Record<string, boolean> = {};
      rows.forEach((row) => {
        next[row.keyPath] = prev[row.keyPath] || false;
      });
      return next;
    });
  }, [rows]);

  const handleInputChange = useCallback((row: TranslationRowModel, localeIndex: number, value: string) => {
    setIsDirty(true);
    setEditedJson((current) =>
      current.map((locale, index) => {
        if (index !== localeIndex) return locale;
        return {
          ...locale,
          content: setNestedValue(row.keyPath, value, locale.content as Record<string, unknown>),
        };
      })
    );
  }, []);

  const handleClearRow = useCallback((row: TranslationRowModel) => {
    setIsDirty(true);
    setEditedJson((current) =>
      current.map((locale) => ({
        ...locale,
        content: setNestedValue(row.keyPath, "", locale.content as Record<string, unknown>),
      }))
    );
  }, []);

  const handleCopyKey = (keyPath: string) => navigator.clipboard?.writeText(keyPath);

  const toggleRowExpansion = useCallback((keyPath: string) => {
    setExpandedKeys((prev) => ({ ...prev, [keyPath]: !prev[keyPath] }));
  }, []);

  const toggleAllRows = useCallback(() => {
    setAreAllExpanded((prev) => {
      const nextValue = !prev;
      setExpandedKeys((current) =>
        Object.fromEntries(Object.keys(current).map((key) => [key, nextValue]))
      );
      return nextValue;
    });
  }, []);

  const truncateKey = (value: string) => {
    const parts = value.split(".");
    if (parts.length <= 2) return value;
    return `${parts[0]}...${parts[parts.length - 1]}`;
  };

  const handlePublish = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      onUpdate(editedJson);
      setIsDirty(false);
    },
    [onUpdate, editedJson]
  );

  const handleSave = useCallback(
    (event: React.MouseEvent, commitMessage: string) => {
      event.preventDefault();
      onSave(editedJson, commitMessage);
      setIsDirty(false);
    },
    [onSave, editedJson]
  );

  const emptyStateText = useMemo(() => {
    if (!localesList.length) return "No languages available in editor payload.";
    if (searchQuery.trim() || quickFilters.showOnlyChanged || quickFilters.showEmpty || quickFilters.showMissing) {
      return "No rows match current filters.";
    }
    return "No translation rows found.";
  }, [localesList.length, searchQuery, quickFilters]);

  return (
    <form className="space-y-3">
      <div className="rounded border">
        <div className="max-h-[58vh] overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 z-20 bg-background">
              <TableRow>
                <TableHead className="sticky left-0 z-30 w-[220px] min-w-[220px] bg-background">
                  <div className="flex items-center justify-between">
                    <span>Translation key</span>
                    <Button type="button" size="icon" variant="ghost" onClick={toggleAllRows}>
                      {areAllExpanded ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </TableHead>
                {editedJson.map((locale, idx) => (
                  <TableHead key={`${locale.metadata.path}-${idx}`} className="min-w-[360px] py-3">
                    <LangBlock lang={locale.metadata.name} />
                  </TableHead>
                ))}
                <TableHead className="w-[68px] min-w-[68px] text-right">
                  <span className="sr-only">Row actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.keyPath} className={row.hasChanged ? "bg-amber-50/40" : ""}>
                  <TableCell className="sticky left-0 z-10 w-[220px] min-w-[220px] align-top bg-background">
                    <div className="space-y-2">
                      <button
                        type="button"
                        className="break-all text-left text-xs font-medium hover:underline"
                        onClick={() => toggleRowExpansion(row.keyPath)}
                      >
                        {expandedKeys[row.keyPath] ? row.keyPath : truncateKey(row.keyPath)}
                      </button>
                      <div className="flex gap-1">
                        {row.hasChanged ? (
                          <span className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] text-amber-700">
                            <Sparkles className="h-3 w-3" /> changed
                          </span>
                        ) : null}
                        {row.hasEmpty ? (
                          <span className="inline-flex rounded border px-2 py-0.5 text-[10px] text-red-600">
                            empty
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </TableCell>

                  {editedJson.map((locale, localeIndex) => {
                    const value = getNestedValue(
                      row.keyPath,
                      locale.content as Record<string, unknown>
                    );

                    return (
                      <TableCell
                        key={`${row.keyPath}-${locale.metadata.path}`}
                        className={quickFilters.compact ? "py-1" : "py-2"}
                      >
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <DyTextarea
                                className={quickFilters.compact ? "min-h-[56px]" : "min-h-[88px]"}
                                value={value}
                                onChange={(event) =>
                                  handleInputChange(row, localeIndex, event.target.value)
                                }
                              />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-md whitespace-pre-wrap text-xs">
                              {value || "(empty)"}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    );
                  })}

                  <TableCell className="w-[68px] min-w-[68px] align-top">
                    <div className="flex items-end justify-end">
                      <div className="flex flex-col items-end gap-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() => handleCopyKey(row.keyPath)}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="text-xs">Copy key</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() => handleClearRow(row)}
                            >
                              <Eraser className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="text-xs">Clear row values</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">{emptyStateText}</div>
        ) : null}
      </div>

      <JsonEditorActions
        onSave={handleSave}
        onPublish={handlePublish}
        hasCommits={hasCommits}
        isBusy={isBusy}
        isDirty={isDirty}
        resultCount={rows.length}
        activeBranch={activeBranch}
        defaultBranch={defaultBranch}
      />
    </form>
  );
};

export default JsonEditorForm;
